import React, { useMemo } from 'react';

export interface PaginationProps {
  /** 当前页码（从 1 开始） */
  currentPage: number;
  /** 总页数 */
  totalPages: number;
  /** 页码切换回调函数 */
  onPageChange: (page: number) => void;
  /** 是否处于加载状态（加载时禁用点击与调低透明度） */
  loading?: boolean;
  /** 自定义容器类名 */
  className?: string;
  /** 总页数 <= 1 时是否自动隐藏，默认 true */
  hideOnSinglePage?: boolean;
}

/**
 * 统一通用底部分页器组件
 */
export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  loading = false,
  className = '',
  hideOnSinglePage = false,
}) => {
  const safeTotalPages = Math.max(1, totalPages || 1);

  // 当显式配置了 hideOnSinglePage = true 且总页数 <= 1 时返回 null
  if (hideOnSinglePage && safeTotalPages <= 1) {
    return null;
  }

  // 智能计算展示的页码数字与省略符号
  const pageNumbers = useMemo(() => {
    if (safeTotalPages <= 7) {
      return Array.from({ length: safeTotalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];
    const delta = 1; // 当前页前后展示的页码个数

    const left = currentPage - delta;
    const right = currentPage + delta;

    for (let i = 1; i <= safeTotalPages; i++) {
      if (i === 1 || i === safeTotalPages || (i >= left && i <= right)) {
        pages.push(i);
      } else if (i === left - 1 || i === right + 1) {
        pages.push('...');
      }
    }

    // 去重相邻的 '...'
    const result: (number | string)[] = [];
    pages.forEach((p) => {
      if (p === '...' && result[result.length - 1] === '...') {
        return;
      }
      result.push(p);
    });

    return result;
  }, [currentPage, safeTotalPages]);

  const handlePrev = () => {
    if (currentPage > 1 && !loading) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < safeTotalPages && !loading) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number | string) => {
    if (typeof page === 'number' && page !== currentPage && !loading) {
      onPageChange(page);
    }
  };

  return (
    <div
      className={`mt-auto pt-6 pb-2 flex items-center justify-center gap-2 transition-opacity duration-200 ${
        loading ? 'opacity-50 pointer-events-none' : 'opacity-100'
      } ${className}`}
    >
      {/* 上一页按钮 */}
      <button
        type="button"
        onClick={handlePrev}
        disabled={currentPage === 1 || loading}
        aria-label="上一页"
        className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-foreground-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer shadow-2xs"
      >
        <i className="ri-arrow-left-s-line text-lg"></i>
      </button>

      {/* 页码列表 */}
      {pageNumbers.map((page, index) => {
        if (page === '...') {
          return (
            <span
              key={`ellipsis-${index}`}
              className="w-9 h-9 flex items-center justify-center text-foreground-400 text-xs select-none"
            >
              <i className="ri-more-fill text-sm"></i>
            </span>
          );
        }

        const isCurrent = page === currentPage;
        return (
          <button
            key={page}
            type="button"
            onClick={() => handlePageClick(page)}
            disabled={loading}
            className={`w-9 h-9 rounded-xl text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
              isCurrent
                ? 'bg-emerald-600 text-white shadow-xs hover:bg-emerald-700'
                : 'bg-white border border-gray-200 text-foreground-700 hover:bg-gray-50 hover:border-gray-300 shadow-2xs'
            }`}
          >
            {page}
          </button>
        );
      })}

      {/* 下一页按钮 */}
      <button
        type="button"
        onClick={handleNext}
        disabled={currentPage === safeTotalPages || loading}
        aria-label="下一页"
        className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-foreground-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer shadow-2xs"
      >
        <i className="ri-arrow-right-s-line text-lg"></i>
      </button>
    </div>
  );
};

export default Pagination;
